<template>
  <!-- Breadcrumbs -->
  <BaseContainer>
    <V3Breadcrumbs
      :breadcrumb-items="[
        { type: 'link', text: $t('products.home'), to: '/' } as const,
        { type: 'link', text: $t('products.allStyle'), to: '/styles' } as const
      ]"
      :current-page-text="
        search ? `${$t('products.search')}: ${search}` : undefined
      "
    />
  </BaseContainer>

  <!-- Banner -->
  <section v-if="!!widgetData?.mobileImageUrl || !!widgetData?.imageUrl">
    <div
      v-if="!!widgetData?.mobileImageUrl || !!widgetData?.imageUrl"
      :key="widgetData?.mobileImageUrl"
      class="relative block bg-primary-tangerine lg:hidden"
    >
      <img
        :src="widgetData?.mobileImageUrl || widgetData?.imageUrl"
        alt="cover image"
        class="top-0 left-0 object-cover object-center w-full h-full"
      />
    </div>
    <div
      v-if="!!widgetData?.imageUrl"
      :key="widgetData?.imageUrl"
      class="relative hidden bg-primary-tangerine lg:block"
    >
      <img
        :src="widgetData?.imageUrl"
        alt="cover image"
        class="top-0 left-0 object-cover object-center w-full h-full"
      />
    </div>
  </section>
  <section v-else :class="'bg-primary-tangerine relative'">
    <img
      alt="all styles"
      src="/images/banner-faqs.jpg"
      class="top-0 left-0 object-cover object-center w-full h-full"
    />
  </section>

  <!-- Header -->
  <BaseContainer>
    <header class="mt-10">
      <h1 class="text-2xl font-bold md:font-normal md:text-5xl">
        {{ $t('styles.shopByStyle') }}
      </h1>
      <p class="text-xl max-w-[720px] mt-2">
        {{ $t('styles.shopByStyleDesc') }}
      </p>
    </header>
  </BaseContainer>

  <!-- Collections -->
  <section class="py-16 space-y-16">
    <CollectionListSwiper
      v-for="(collection, index) in collections"
      :key="index"
      :slide-key="`collection-style-` + index"
      :title="
        locale === 'en'
          ? collection[0].headerEn || ''
          : collection[0].headerTh || collection[0].headerEn || ''
      "
      :data="collection"
      recommendation
    />

    <!-- 🔥 Braze Product Recommendations (Feature Flag Controlled) -->
    <template v-if="hasStylePageCards">
      <section v-for="card in stylePageCardsWithProducts" :key="card.id">
        <BrazeProductRecommendations
          :title="card.title"
          :title_en="card.featureFlagConfig?.title_en"
          :title_th="card.featureFlagConfig?.title_th"
          :title_tag_en="card.featureFlagConfig?.title_tag_en"
          :title_tag_th="card.featureFlagConfig?.title_tag_th"
          :title_tag_color="card.featureFlagConfig?.title_tag_color"
          :title_tag_font_color="card.featureFlagConfig?.title_tag_font_color"
          :fetched-products="card.fetchedProducts"
          :slides-per-view="
            card.featureFlagConfig?.slidesPerView || card.slidesPerView
          "
          :slides-per-group="
            card.featureFlagConfig?.slidesPerGroup || card.slidesPerGroup
          "
          :space-between="
            card.featureFlagConfig?.spaceBetween || card.spaceBetween
          "
          :tag="card.productRecTag"
          :tag-color="card.productRecTagColor"
          :product_tag_th="card.productTagTh"
          :product_tag_en="card.productTagEn"
          :product_tag_color="card.productTagColor"
          :product_tag_font_color="card.productTagFontColor"
        />
      </section>
    </template>
    <BrazeContentCardsList
      v-else-if="brazeCardsLoaded && !hasStylePageCards"
      layout="carousel"
      type="style_page"
      :show-debug="false"
      :only-active="true"
    />

    <V2ProductListSwiper
      v-if="hotPicks && hotPicks.length > 0"
      :title="hotPicksTitle"
      slide-key="product-style-a"
      :data="hotPicks"
      :data-row2="hotPicksRow2"
      :banner-desktop-url="hotPicksBannerDesktopUrl"
      :banner-mobile-url="hotPicksBannerMobileUrl"
      recommendation
      :style="true"
    />
  </section>
  <ContentBelowProductList
    v-if="
      locale === 'en'
        ? dataPage.contentDescriptionEn
          ? true
          : false
        : dataPage.contentDescriptionTh
          ? true
          : false
    "
    :description="dataPage"
  />
</template>

<script lang="ts" setup>
// TODO: revise script code
import { find } from 'lodash-es'
import { cmsRepository } from '~/repository/cmsRepository'
import { seoRepository } from '~/repository/seoRepository'

const route = useRoute()

// Import Braze composables
const {
  cards: brazeCards,
  loading: brazeCardsLoading,
  fetchCards
} = useBrazeContentCards()
const { isEnabled, getStylePageCardConfig } = useBrazeFeatureFlags()
const search = computed(() => (route.query.search as string) || '')

const config = useRuntimeConfig()

const { locale, t } = useI18n()
const { $serverApi } = useNuxtApp()
const seoRepo = seoRepository($serverApi)
const cmsRepo = cmsRepository($serverApi)

const toMediaUrl = (path: string | null | undefined): string => {
  if (!path) return ''
  if (path.startsWith('http')) return path
  return `${config.public.mediaUrl}/${path}`
}

// LEGACY: previous Algolia-search-based fetch for the style-page hot picks widget.
// Superseded by GET /v2/storefront/widgets/{slug}/products below — kept for reference/rollback, not called.
const legacyFetchHotPicks = async () => {
  const { search: algoliaSearchHotPicks } = useAlgoliaSearch<AlgoliaProductV2>(
    config.public.algoliaIndexNext
  )
  const result = await algoliaSearchHotPicks({
    query: 'collection-hot-picks-by-style',
    requestOptions: { page: 0, hitsPerPage: 20 }
  })
  return result.hits || []
}

const { data: hotPicksData } = await useAsyncData(
  'widgetProductsCollectionHotPicksByStyle',
  async () => {
    const response: any = await cmsRepo.getWidgetProducts('collection-hot-picks-by-style')
    const items = response?.data?.groups?.[0]?.items ?? []
    const itemsRow2 = response?.data?.groups?.[1]?.items ?? []
    return { items, itemsRow2, meta: response?.data?.meta ?? {} }
  }
)
const hotPicks = computed(() => hotPicksData.value?.items ?? [])
const hotPicksRow2 = computed(() => hotPicksData.value?.itemsRow2 ?? [])
const hotPicksBannerDesktopUrl = computed(() => toMediaUrl(hotPicksData.value?.meta?.desktop_url))
const hotPicksBannerMobileUrl = computed(() => toMediaUrl(hotPicksData.value?.meta?.mobile_url))
const hotPicksTitle = computed(() =>
  locale.value === 'th'
    ? hotPicksData.value?.meta?.header_th || t('landing.titleHotProduct')
    : hotPicksData.value?.meta?.header_en || t('landing.titleHotProduct')
)

// 🔥 Import Braze composables
const { trackStylesPageView } = useCaseClubEvents()

const { dataPage } = await usePageDescription()

const { data: pageDataSeo } = await useAsyncData(
  'getSeoProductByAllStylePage',
  async () => {
    const data = await seoRepo.getSeo('product-list-style?category=all-styles')
    return data
  }
)

const seoData = computed(() => {
  const defaultSeo = {
    title:
      locale.value === 'en'
        ? pageDataSeo.value?.meta_title?.en
        : pageDataSeo.value?.meta_title?.th,
    description:
      locale.value === 'en'
        ? pageDataSeo.value?.meta_description?.en
        : pageDataSeo.value?.meta_description?.th,
    ogImage: pageDataSeo.value?.og_image,
    keywords: pageDataSeo.value?.meta_keyword
  }

  return defaultSeo
})

useSeoMeta({
  title: seoData.value.title || undefined,
  description: seoData.value.description || undefined,
  ogImage: seoData.value.ogImage ? String(seoData.value.ogImage) : undefined,
  keywords: seoData.value.keywords || undefined
})

const { data: collections } = await useAsyncData<
  {
    headerEn?: string
    headerTh?: string
    imageUrl?: string
    mobileImageUrl?: string
    titleEn: string
    titleTh: string
    descriptionEn: string
    descriptionTh?: string
    href?: string
  }[][]
>('getStylePageCollection', async () => {
  const response = await cmsRepo.getWidget('style-page-collection')
  if (response.data.data.items) return response.data.data.items
  return null
})

const { data: bannerData } = await useAsyncData<
  {
    imageUrl: string
    mobileImageUrl?: string
    pathname: string
  }[]
>('getBanners/', async () => {
  const response = await cmsRepo.getWidget('banners')
  return response.data.data.items
})

const widgetData = computed(() => {
  const banner = find(bannerData.value, (item) =>
    item.pathname
      ?.split(',')
      .map((item) => item.trim())
      .includes(route.fullPath.replace('/en', '').replace('/th', ''))
  )

  return banner
})

// 🔥 Style Page Cards Logic (Feature Flag Controlled)
const stylePageCards = computed(() => {
  if (!isEnabled('style_page_show_recommendations', true)) {
    return []
  }
  // Get all style page cards - ใช้เงื่อนไขที่หลวมขึ้น
  const allCards = brazeCards.value.filter(
    (c: any) => {
      // ตรวจสอบว่าเป็น style page (ใช้เงื่อนไขใดเงื่อนไขหนึ่ง)
      const isStylePage = c.bokey?.startsWith('style_') || 
                         c.pageType === 'style_page' ||
                         c.type === 'style_page' ||
                         c.bokey === 'style_general'
      
      const notDismissed = !c.dismissed
      return isStylePage && notDismissed
    }
  )
  
  if (allCards.length === 0) return []

  // Get config from feature flag
  const config = getStylePageCardConfig()
  if (config && config.length > 0) {
    // 🔥 Filter and sort by feature flag config + merge config into cards
    const filtered = config
      .map((item: any) => {
        const card = allCards.find((c: any) => c.cardKey === item.key)
        if (!card) {
          return undefined
        }
        return {
          ...card,
          featureFlagConfig: item
        }
      })
      .filter((c: any): c is NonNullable<typeof c> => c !== undefined)

    return filtered
  }

  // No config = show all cards (sorted by Braze priority)
  return allCards
})

const hasStylePageCards = computed(() => stylePageCards.value.length > 0)
const brazeCardsLoaded = computed(() => !brazeCardsLoading.value)

// 🔥 Batch fetch products for all cards (ยิง API ครั้งเดียว)
const stylePageCardsWithProducts = ref<any[]>([])
const fetchAllProducts = async () => {
  if (stylePageCards.value.length === 0) {
    stylePageCardsWithProducts.value = []
    return
  }

  const { parseProductIds, fetchProductsByIds } = useBrazeProducts()

  // 1. รวม product IDs จาก cards ทั้งหมด
  const allIds: string[] = []
  const cardIdMapping: Record<string, string[]> = {} // map card ID -> product IDs

  stylePageCards.value.forEach((card: any) => {
    const ids = parseProductIds(card.productsData)
    if (ids.length > 0) {
      cardIdMapping[card.id] = ids
      allIds.push(...ids)
    }
  })

  // 2. ยิง API ครั้งเดียวด้วย IDs ทั้งหมด
  const allProducts = await fetchProductsByIds(allIds)

  // 3. สร้าง product map สำหรับ lookup ง่ายๆ
  const productMap = new Map(allProducts.map((p) => [p.id, p]))

  // 4. แจกผลลัพธ์กลับไปให้แต่ละ card
  stylePageCardsWithProducts.value = stylePageCards.value.map((card: any) => {
    const cardProductIds = cardIdMapping[card.id] || []
    const fetchedProducts = cardProductIds
      .map((id) => productMap.get(id))
      .filter((p) => p !== undefined)

    return {
      ...card,
      fetchedProducts
    }
  })
}

// Watch stylePageCards และ fetch products เมื่อมีการเปลี่ยนแปลง
watch(
  () => stylePageCards.value,
  async () => {
    await fetchAllProducts()
  },
  { immediate: true }
)

// 🔥 Track Styles page view
const { syncContentCards } = useBrazeContentCards()

onMounted(async () => {
  await fetchCards()
  try {
    trackStylesPageView()
  } catch (error) {
  }
})
</script>
